import { IAssistantMessage, IQuestion, IQuestionOption } from '../types';
import { getAnswersFromMessages, getFormValuesFromMessages, shouldShowQuestion } from './displayCondition';

/**
 * Removes old dependent questions from changed messages that are no longer valid
 *
 * This utility compares the changed messages with original messages to identify:
 * 1. Questions that have changed their answer (different selected optionId)
 * 2. Questions in changed messages that depend on the OLD answer
 * 3. Questions that are valid for OLD product but not NEW product
 * 4. Removes those old dependent questions from the result
 *
 * @param changedMessages - Messages after editing (includes draft messages merged with original)
 * @param originalMessages - Original messages before editing
 * @param questionFlow - Complete question flow to check dependencies
 * @param currentProduct - Current selected product option
 * @returns Filtered messages with old dependents removed
 */
export const removeOldDependentQuestions = (
    changedMessages: IAssistantMessage[],
    originalMessages: IAssistantMessage[],
    questionFlow: IQuestion[],
    currentProduct?: IQuestionOption
): IAssistantMessage[] => {

    // Step 1: Build maps of questionId -> selected optionIds for both old and new
    const changedAnswers = new Map<string, Set<string>>();
    const originalAnswers = new Map<string, Set<string>>();

    changedMessages.forEach(msg => {
        if (msg.type === 'user' && msg.question?.questionId && msg.response?.answer) {
            const optionIds = new Set(msg.response.answer.map(opt => opt.optionId));
            changedAnswers.set(msg.question.questionId, optionIds);
        }
    });

    originalMessages.forEach(msg => {
        if (msg.type === 'user' && msg.question?.questionId && msg.response?.answer) {
            const optionIds = new Set(msg.response.answer.map(opt => opt.optionId));
            originalAnswers.set(msg.question.questionId, optionIds);
        }
    });

    // Step 2: Find questions that changed their answer
    const changedQuestions = new Map<string, { oldOptionIds: Set<string>, newOptionIds: Set<string> }>();

    changedAnswers.forEach((newOptionIds, questionId) => {
        const oldOptionIds = originalAnswers.get(questionId);

        if (oldOptionIds) {
            // Check if the answer actually changed
            const oldArray = Array.from(oldOptionIds).sort();
            const newArray = Array.from(newOptionIds).sort();

            if (oldArray.join(',') !== newArray.join(',')) {
                changedQuestions.set(questionId, {
                    oldOptionIds,
                    newOptionIds
                });
            }
        }
    });

    if (changedQuestions.size === 0) {
        return changedMessages;
    }

    // Step 3: Build a set of optionIds to REMOVE (old optionIds from changed questions)
    const oldOptionIdsToRemove = new Set<string>();
    changedQuestions.forEach(({ oldOptionIds }) => {
        oldOptionIds.forEach(optionId => oldOptionIdsToRemove.add(optionId));
    });


    // Step 4a: Build a map of categories and their question IDs in changed messages
    const categoryToQuestionIds = new Map<string, Set<string>>();
    changedMessages.forEach(msg => {
        if (msg.type === 'user' && msg.question?.category && msg.question?.questionId) {
            if (!categoryToQuestionIds.has(msg.question.category)) {
                categoryToQuestionIds.set(msg.question.category, new Set());
            }
            categoryToQuestionIds.get(msg.question.category)!.add(msg.question.questionId);
        }
    });

    // Step 4b: Build the category/frame-scoped answers + form values for the
    // current (changed) messages, for the real shouldShowQuestion re-check below.
    const currentAnswersByCategory = getAnswersFromMessages(changedMessages);
    const currentFormValues = getFormValuesFromMessages(changedMessages);

    // Step 5: Filter out messages that should be removed
    const filteredMessages = changedMessages.filter(msg => {
        if (msg.type !== 'user' || !msg.question) {
            return true; // Keep non-user messages
        }

        const question = msg.question;

        // Check 1: Is this question valid for the current product?
        if (currentProduct && question.displayCondition?.validForProducts) {
            const validProducts = question.displayCondition.validForProducts;
            if (validProducts.length > 0 && !validProducts.includes(currentProduct.optionId)) {
                return false;
            }
        }

        // Check 2: If there are multiple questions with the same category, keep only the valid one
        if (question.category) {
            const questionsInCategory = categoryToQuestionIds.get(question.category);
            if (questionsInCategory && questionsInCategory.size > 1) {
                // Multiple questions for same category - check product validity
                if (currentProduct && question.displayCondition?.validForProducts) {
                    const validProducts = question.displayCondition.validForProducts;
                    if (validProducts.length > 0 && !validProducts.includes(currentProduct.optionId)) {
                        return false;
                    }
                }
            }
        }

        // Check 3: Does this question depend on any of the old optionIds?
        const dependsOnOldOption = checkIfDependsOnOptions(question, oldOptionIdsToRemove);

        if (dependsOnOldOption) {
            // Also check if it depends on any NEW options (keep if it does)
            const newOptionIds = new Set<string>();
            changedQuestions.forEach(({ newOptionIds: opts }) => {
                opts.forEach(id => newOptionIds.add(id));
            });

            const dependsOnNewOption = checkIfDependsOnOptions(question, newOptionIds);

            if (dependsOnNewOption) {
                return true;
            }

            return false;
        }

        // Check 4: Transitive dependency — does this question still pass the real,
        // category/frame-scoped displayCondition evaluator given the current merged
        // answers? This used to be a separate flat, category-agnostic optionId-presence
        // check, which could diverge from shouldShowQuestion (the authoritative
        // evaluator used everywhere else) and wrongly prune valid dependents — e.g.
        // an unrelated Dimension edit reshaping that category's answer entries could
        // make a presence-based dependent like Transom Drop look "unanswered" even
        // though its actual trigger field was never touched. Routing through the same
        // evaluator the rest of the app uses keeps this check from drifting out of sync.
        if (question.displayCondition?.dependsOn?.length) {
            if (!shouldShowQuestion(question, currentAnswersByCategory, currentProduct, false, currentFormValues)) {
                return false;
            }
        }

        // Keep if it passes all checks
        return true;
    });

    return filteredMessages;
};

/**
 * Check if a question depends on any of the given optionIds
 */
const checkIfDependsOnOptions = (question: IQuestion, optionIds: Set<string>): boolean => {
    // Check question-level dependencies
    if (question.displayCondition?.dependsOn) {
        for (const dependency of question.displayCondition.dependsOn) {
            if (dependency.optionIds) {
                for (const optionId of dependency.optionIds) {
                    if (optionIds.has(optionId)) {
                        return true;
                    }
                }
            }
        }
    }

    // Check option-level dependencies
    if (question.options) {
        for (const option of question.options) {
            if (option.displayCondition?.dependsOn) {
                for (const dependency of option.displayCondition.dependsOn) {
                    if (dependency.optionIds) {
                        for (const optionId of dependency.optionIds) {
                            if (optionIds.has(optionId)) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
    }

    return false;
};
